import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/auth-context';
import { RealtimeStatusIndicator } from '@/components/realtime/realtime-status-indicator';
import { ActionBar } from '@/components/layout/action-bar';
import { NotificationsPopover } from '@/components/layout/notifications-popover';
import { MessagesPopover } from '@/components/layout/messages-popover';
import { UserMenuDropdown } from '@/components/layout/user-menu-dropdown';
import { MandateSelector } from '@/components/layout/mandate-selector';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface PageHeaderProps {
  // Title and navigation
  title?: string;
  subtitle?: string;
  showBackButton?: boolean;
  
  // Custom title content (overrides title/subtitle)
  titleContent?: ReactNode;
  
  // Mandate badge (for pages working with specific clients)
  showMandateBadge?: boolean;
  onMandateChange?: (mandateId: string) => void;
  
  // Search
  showSearch?: boolean;
  searchPlaceholder?: string;
  onSearchChange?: (value: string) => void;
  
  // Quick action button
  quickActionLabel?: string;
  onQuickAction?: () => void;
  
  // Custom actions (e.g., for Validation page)
  customActions?: ReactNode;
  
  // Action Bar (context-aware quick actions from tasks)
  showActionBar?: boolean;
}

const getMandateInitials = (name: string) => {
  if (!name) return 'CL';
  const words = name.trim().split(/\s+/);
  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }
  return `${words[0].charAt(0)}${words[1].charAt(0)}`.toUpperCase();
};

const getMandateStatusColor = (status?: string) => {
  switch (status) {
    case 'active': return 'bg-green-100 border-green-300';
    case 'paused': return 'bg-gray-100 border-gray-300';
    default: return 'bg-brand-accent border-brand-border';
  }
};

const getMandateStatusDot = (status?: string) => {
  switch (status) {
    case 'active': return 'bg-green-500';
    case 'paused': return 'bg-gray-400';
    default: return 'bg-yellow-500';
  }
};

export function PageHeader({
  title,
  subtitle,
  titleContent,
  showBackButton = false,
  showMandateBadge = false,
  onMandateChange,
  showSearch = false,
  searchPlaceholder = "Search...",
  onSearchChange,
  quickActionLabel,
  onQuickAction,
  customActions,
  showActionBar = false,
}: PageHeaderProps) {
  const { profile, signOut } = useAuth();
  const navigate = useNavigate();

  const displayName = profile?.first_name && profile?.last_name
    ? `${profile.first_name} ${profile.last_name}`
    : profile?.email || 'User';

  const roleDisplayName = profile?.role === 'admin' ? 'Administrator' : 'Fiduciaire';

  const getProfileInitials = (profile: any) => {
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name[0]}${profile.last_name[0]}`.toUpperCase();
    }
    return profile?.email?.[0]?.toUpperCase() || 'U';
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <>
    <header className="flex justify-between items-center mb-4">
      {/* Left side - Title/Organization/Back button */}
      <div className="flex items-center gap-4">
        {showBackButton && (
          <button 
            onClick={() => navigate(-1)}
            className="p-2 relative bg-brand-secondary rounded-lg w-8 h-8 flex items-center justify-center hover:bg-brand-accent/10 transition-colors"
          >
            <i className="fa-solid fa-arrow-left text-brand-primary text-sm"></i>
          </button>
        )}

        {showMandateBadge ? (
          <MandateSelector />
        ) : titleContent ? (
          titleContent
        ) : (
          <div>
            {subtitle && (
              <p className="text-sm text-brand-text-secondary">{subtitle}</p>
            )}
            {title && (
              <h1 className="text-brand-primary text-3xl font-space-grotesk font-bold">
                {title}
              </h1>
            )}
          </div>
        )}
      </div>

      {/* Right side - Actions and user info */}
      <div className="flex items-center gap-4">
        {/* Search field */}
        {showSearch && (
          <div className="relative w-72">
            <input
              type="text"
              placeholder={searchPlaceholder}
              onChange={(e) => onSearchChange?.(e.target.value)}
              className="w-full h-[38px] pl-4 pr-10 bg-brand-secondary rounded-[20px] text-xs text-brand-text-secondary focus:outline-none focus:ring-2 focus:ring-brand-accent"
            />
            <i className="fa-solid fa-magnifying-glass text-brand-text-secondary absolute right-4 top-1/2 -translate-y-1/2 text-sm"></i>
          </div>
        )}

        {/* Command Palette Hint */}
        <div className="hidden lg:flex items-center gap-2 text-xs text-muted-foreground">
          <kbd className="px-2 py-1 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded-md">
            ⌘K
          </kbd>
          <span>Quick Access</span>
        </div>

        {/* Quick action button */}
        {quickActionLabel && onQuickAction && (
          <Button 
            onClick={onQuickAction}
            className="bg-brand-primary text-white hover:bg-brand-primary/90"
          >
            <i className="fa-solid fa-plus mr-2 text-sm"></i>
            {quickActionLabel}
          </Button>
        )}

        {/* Custom actions (e.g., Validation page actions) */}
        {customActions}

        {/* Icons: Messages, Notifications */}
        <div className="flex items-center gap-2.5">
          <MessagesPopover />
          <NotificationsPopover />
        </div>

        {/* User Menu */}
        <UserMenuDropdown />
      </div>
    </header>

    {/* Action Bar (context-aware quick actions) */}
    {showActionBar && (
      <div className="mb-6 pb-4 border-b border-brand-border">
        <ActionBar maxActions={4} />
      </div>
    )}
    </>
  );
}
